/**
 * Lienzo interno de un bloque contenedor.
 */
import React, { useRef, useState } from 'react';
import WidgetRenderer from './WidgetRenderer';
import MeasuredGrid from './MeasuredGrid';
import { INNER_CANVAS_COLUMNS, isDecorationType, getWidgetStyle, getTextContentStyle } from './engineConfig';
import { widgetZ } from './arrange';
import { preventDragOver } from './fieldActions';

const NestedWidgetCanvas = ({
    container,
    rowHeight = 40,
    selectedIds = [],
    draggingField,
    engineFilters,
    filterValues,
    schemaColumns,
    schemasBySource = {},
    filterDefs = [],
    dataSources = [],
    joins = [],
    onSelect,
    onDelete,
    onDuplicate,
    onLayoutChange,
    onAssignField,
    onFilterValueChange,
    onQueryPatch,
    onPatchChild,
    onExtractChild,
    onExtractDragChange
}) => {
    const innerWidgets = container?.children?.widgets || [];
    const innerLayout = container?.children?.layout || [];
    const cols = INNER_CANVAS_COLUMNS;
    const blockStyle = getWidgetStyle(container);
    const showChildHeaders = blockStyle.showChildHeaders !== false;
    const gap = Math.max(0, Number(blockStyle.innerGap ?? 0));
    const pad = Math.max(0, Number(blockStyle.innerPadding ?? 0));
    const wrapRef = useRef(null);
    const extractRef = useRef(null);
    const skipLayoutRef = useRef(false);
    const [leaving, setLeaving] = useState(false);

    const isOutside = (event) => {
        const el = wrapRef.current;
        if (!el || !event) return false;
        const x = event.clientX;
        const y = event.clientY;
        if (x == null || y == null) return false;
        const r = el.getBoundingClientRect();
        const slack = 10;
        return x < r.left - slack || x > r.right + slack || y < r.top - slack || y > r.bottom + slack;
    };

    const setLeavingState = (next) => {
        setLeaving(next);
        onExtractDragChange && onExtractDragChange(next);
    };

    if (innerWidgets.length === 0) {
        return (
            <div style={styles.empty}>
                Selecciona este bloque y pulsa + Texto, + Barras o + Tabla para armar el sublienzo.
            </div>
        );
    }

    return (
        <div
            ref={wrapRef}
            style={{
                ...styles.wrap,
                padding: pad,
                overflow: 'visible',
                outline: leaving ? '2px dashed #e65100' : 'none',
                backgroundColor: 'rgba(255,255,255,0.35)',
                backgroundImage: 'radial-gradient(circle, rgba(60,64,67,0.16) 1px, transparent 1.15px)',
                backgroundSize: '8px 8px',
            }}
        >
            {leaving && (
                <div style={styles.leaveHint}>Suelta fuera del bloque para sacarlo al lienzo</div>
            )}
            <MeasuredGrid
                className="inner-layout"
                layout={innerLayout}
                cols={cols}
                rowHeight={rowHeight}
                margin={[gap, gap]}
                isDraggable
                isResizable
                compactType={null}
                resizeHandles={['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']}
                draggableHandle=".nested-drag-handle"
                draggableCancel=".nested-text-editor,textarea,input,button,.react-resizable-handle"
                onDrag={(_layout, _old, item, _ph, event) => {
                    const outside = isOutside(event);
                    if (outside && item?.i) {
                        extractRef.current = { id: item.i, clientX: event.clientX, clientY: event.clientY };
                        skipLayoutRef.current = true;
                        if (!leaving) setLeavingState(true);
                    } else {
                        extractRef.current = null;
                        skipLayoutRef.current = false;
                        if (leaving) setLeavingState(false);
                    }
                }}
                onDragStop={(nextLayout, _old, item, _ph, event) => {
                    const pending = extractRef.current;
                    const outside = isOutside(event) || !!pending;
                    extractRef.current = null;
                    if (leaving) setLeavingState(false);
                    if (outside && onExtractChild && (pending?.id || item?.i)) {
                        skipLayoutRef.current = true;
                        onExtractChild(
                            pending?.id || item.i,
                            event?.clientX ?? pending?.clientX,
                            event?.clientY ?? pending?.clientY
                        );
                        window.setTimeout(() => { skipLayoutRef.current = false; }, 0);
                        return;
                    }
                    skipLayoutRef.current = false;
                    if (!onLayoutChange || !nextLayout) return;
                    const compact = (items) => (items || []).map(entry => `${entry.i}:${entry.x}:${entry.y}:${entry.w}:${entry.h}`).join('|');
                    if (compact(nextLayout) === compact(innerLayout)) return;
                    onLayoutChange(nextLayout);
                }}
                onLayoutChange={(current) => {
                    if (!onLayoutChange || skipLayoutRef.current) return;
                    const compact = (items) => (items || []).map(item => `${item.i}:${item.x}:${item.y}:${item.w}:${item.h}`).join('|');
                    if (compact(current) === compact(innerLayout)) return;
                    onLayoutChange(current);
                }}
            >
                {innerWidgets.map(child => {
                    const childStyle = getWidgetStyle(child);
                    return (
                    <div
                        key={child.id}
                        className="nested-drag-handle"
                        style={{
                            ...styles.card,
                            backgroundColor: childStyle.cardBackground || '#fff',
                            borderWidth: 1,
                            borderStyle: 'solid',
                            borderColor: '#c4c7cc',
                            borderRadius: childStyle.cardRadius ?? 8,
                            outline: selectedIds.includes(child.id)
                                ? '2px solid #5E35B1'
                                : '1px solid #e0d4f7',
                            overflow: child.type === 'text' ? 'auto' : 'hidden',
                            zIndex: widgetZ(child) + 1,
                            cursor: 'move'
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect && onSelect(child.id, e);
                        }}
                    >
                        {showChildHeaders && (
                        <div
                            className="nested-drag-chrome"
                            style={styles.chrome}
                        >
                            {child.style?.showTitle !== false && (
                                <span style={styles.title}>{child.title || (child.type === 'text' ? 'Texto' : 'Sin título')}</span>
                            )}
                            {child.style?.showTitle === false && <span style={{ flex: 1 }} />}
                            <span style={styles.type}>{child.type === 'text' ? 'texto' : child.type}</span>
                            <button
                                type="button"
                                style={styles.iconBtn}
                                title="Duplicar"
                                onClick={(e) => { e.stopPropagation(); onDuplicate && onDuplicate(child.id); }}
                            >⧉</button>
                            <button
                                type="button"
                                style={styles.iconBtn}
                                title="Sacar del bloque"
                                onClick={(e) => { e.stopPropagation(); onExtractChild && onExtractChild(child.id); }}
                            >↗</button>
                            <button
                                type="button"
                                style={styles.iconBtn}
                                onClick={(e) => { e.stopPropagation(); onDelete && onDelete(child.id, e); }}
                            >×</button>
                        </div>
                        )}
                        {!showChildHeaders && (
                            <div className="nested-drag-chrome" style={styles.chromeSlim} title="Arrastra el elemento; suéltalo fuera del bloque para sacarlo">
                                <span style={styles.type}>{child.type === 'text' ? 'texto' : child.type}</span>
                                <span style={{ flex: 1 }} />
                                <button
                                    type="button"
                                    style={styles.iconBtn}
                                    title="Sacar del bloque"
                                    onClick={(e) => { e.stopPropagation(); onExtractChild && onExtractChild(child.id); }}
                                >↗</button>
                                <button type="button" style={styles.iconBtn} onClick={(e) => { e.stopPropagation(); onDelete && onDelete(child.id, e); }}>×</button>
                            </div>
                        )}
                        <div className="nested-widget-body" style={styles.body}>
                            {draggingField && !isDecorationType(child.type) && (
                                <div style={styles.dropOverlay}>
                                    {child.type === 'slicer' ? (
                                        <div
                                            style={styles.dropMet}
                                            onDragOver={preventDragOver}
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onAssignField && onAssignField(child, draggingField, 'dimension');
                                            }}
                                        >
                                            Campo del filtro
                                        </div>
                                    ) : (
                                        <>
                                            {child.type !== 'kpi' && (
                                                <div
                                                    style={styles.dropDim}
                                                    onDragOver={preventDragOver}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onAssignField && onAssignField(child, draggingField, 'dimension');
                                                    }}
                                                >
                                                    Dimensión
                                                </div>
                                            )}
                                            <div
                                                style={styles.dropMet}
                                                onDragOver={preventDragOver}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onAssignField && onAssignField(child, draggingField, 'metric');
                                                }}
                                            >
                                                Métrica
                                            </div>
                                            <div
                                                style={styles.dropFilter}
                                                onDragOver={preventDragOver}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onAssignField && onAssignField(child, draggingField, 'filter');
                                                }}
                                            >
                                                Filtro fijo
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            {child.type === 'text' ? (
                                <textarea
                                    className="nested-text-editor"
                                    value={child.content || ''}
                                    placeholder="Escribe el texto aquí…"
                                    onChange={e => onPatchChild && onPatchChild(child.id, { content: e.target.value })}
                                    onClick={e => e.stopPropagation()}
                                    onMouseDown={e => e.stopPropagation()}
                                    onKeyDown={e => e.stopPropagation()}
                                    style={{
                                        ...styles.textEditor,
                                        ...getTextContentStyle(childStyle, { editor: true }),
                                        flex: '0 1 auto'
                                    }}
                                />
                            ) : (
                            <WidgetRenderer
                                nested
                                canvasRowHeight={rowHeight}
                                config={child}
                                engineFilters={engineFilters}
                                showTitle={false}
                                filterValues={filterValues}
                                schemaColumns={schemasBySource[child.query?.dataSourceId]?.columns || schemaColumns}
                                schemasBySource={schemasBySource}
                                filterDefs={filterDefs}
                                dataSources={dataSources}
                                joins={joins}
                                onFilterValueChange={onFilterValueChange}
                                onQueryPatch={(patch) => onQueryPatch && onQueryPatch(child.id, patch)}
                                exportable
                            />
                            )}
                        </div>
                    </div>
                    );
                })}
            </MeasuredGrid>
        </div>
    );
};

const styles = {
    wrap: {
        flex: 1,
        minHeight: 0,
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        borderRadius: 0,
        position: 'relative'
    },
    leaveHint: {
        position: 'absolute',
        top: 6,
        left: 6,
        right: 6,
        zIndex: 8,
        pointerEvents: 'none',
        backgroundColor: 'rgba(230, 81, 0, 0.92)',
        color: '#fff',
        fontSize: '0.72rem',
        fontWeight: 700,
        textAlign: 'center',
        padding: '4px 8px',
        borderRadius: 6
    },
    empty: {
        color: '#3c4043',
        fontSize: '0.82rem',
        padding: '10px 8px',
        textAlign: 'center',
        backgroundColor: '#fafafa',
        borderWidth: 1,
        borderStyle: 'dashed',
        borderColor: '#c4c7cc',
        borderRadius: 8,
    },
    card: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        height: '100%',
        backgroundColor: '#fff'
    },
    chrome: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 8px',
        backgroundColor: '#f6f2fb',
        cursor: 'move',
        flex: '0 0 auto',
        flexShrink: 0
    },
    chromeSlim: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '2px 6px',
        backgroundColor: 'transparent',
        cursor: 'move',
        flex: '0 0 auto',
        flexShrink: 0
    },
    textEditor: {
        flex: 1,
        width: '100%',
        minHeight: 0,
        border: 'none',
        resize: 'none',
        outline: 'none',
        padding: '8px',
        backgroundColor: 'transparent',
        fontFamily: 'inherit',
        lineHeight: 1.45,
        boxSizing: 'border-box'
    },
    type: { fontSize: '0.65rem', color: '#7b1fa2', textTransform: 'uppercase' },
    title: { flex: 1, fontSize: '0.75rem', fontWeight: 700, color: '#4a148c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    iconBtn: {
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        color: '#5E35B1',
        fontSize: '0.9rem',
        lineHeight: 1,
        padding: '0 2px'
    },
    body: {
        flex: 1,
        minHeight: 0,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch'
    },
    dropOverlay: {
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        display: 'flex',
        backgroundColor: 'rgba(255,255,255,0.92)'
    },
    dropDim: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#174ea6', borderRight: '1px dashed #dadce0' },
    dropMet: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#137333' },
    dropFilter: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', color: '#b06000', borderLeft: '1px dashed #dadce0' }
};

export default NestedWidgetCanvas;
